/**
 * Security utilities for cache validation and access control
 * Ensures multi-tenant isolation and role-based access
 */

import { UserContext, validateCacheKeyAccess, parseCacheKey } from '@/stores/cacheKeys';
import { CacheEntry } from '@/stores/cacheConfig';

/**
 * Security validation for cache operations
 */
export class CacheSecurityValidator {
  /**
   * Validate user access to cache key
   */
  static validateAccess(key: string, userContext: UserContext): boolean {
    return validateCacheKeyAccess(key, userContext);
  }

  /**
   * Validate staff access to branch-specific data
   */
  static validateBranchAccess(
    branchId: string, 
    userContext: UserContext
  ): boolean {
    // Owners have access to all branches in their restaurant
    if (!userContext.isStaff) {
      return true;
    }

    // Staff users need explicit branch access
    if (!userContext.allowedBranches || userContext.allowedBranches.length === 0) {
      // Empty array means access to all branches
      return true;
    }

    return userContext.allowedBranches.includes(branchId);
  }

  /**
   * Validate cache entry data based on user permissions
   */
  static validateCacheData<T>(
    entry: CacheEntry<T>,
    userContext: UserContext
  ): boolean {
    try {
      const keyInfo = parseCacheKey(entry.key);
      
      // Basic user context validation
      if (!this.validateAccess(entry.key, userContext)) {
        return false;
      }

      // Additional validation based on data type
      switch (keyInfo.dataType) {
        case 'categories':
        case 'menuItems':
        case 'deliveryZones':
        case 'orders':
        case 'tableSessionSummary':
          // These require branch access validation
          const branchIdParam = keyInfo.additionalParams.find(param => 
            param.startsWith('branchId:')
          );
          
          if (branchIdParam) {
            const branchId = branchIdParam.split(':')[1];
            return this.validateBranchAccess(branchId, userContext);
          }
          break;
          
        case 'branches':
          // Branches are restaurant-level, already validated by key access
          return true;
      }

      return true;
    } catch (error) {
      console.error('Cache data validation error:', error);
      return false;
    }
  }

  /**
   * Filter cache entries based on user permissions
   */
  static filterCacheEntries<T>(
    entries: CacheEntry<T>[],
    userContext: UserContext
  ): CacheEntry<T>[] {
    return entries.filter(entry => 
      this.validateCacheData(entry, userContext)
    );
  }

  /**
   * Sanitize cache keys for user context
   */
  static sanitizeCacheKeys(
    keys: string[],
    userContext: UserContext
  ): string[] {
    return keys.filter(key => this.validateAccess(key, userContext));
  }

  /**
   * Generate secure cache version based on user context
   */
  static generateCacheVersion(userContext: UserContext): string {
    // Create a version string that changes when user permissions change
    const permissionHash = userContext.allowedBranches
      .sort()
      .join(',');
    
    const versionData = {
      userId: userContext.userId,
      restaurantId: userContext.restaurantId,
      isStaff: userContext.isStaff,
      permissionHash
    };

    // Simple hash function for version generation
    return btoa(JSON.stringify(versionData)).slice(0, 16);
  }

  /**
   * Validate cache version against current user context
   */
  static validateCacheVersion(
    cacheVersion: string,
    userContext: UserContext
  ): boolean {
    const currentVersion = this.generateCacheVersion(userContext);
    return cacheVersion === currentVersion;
  }

  /**
   * Check if cache entry is potentially sensitive
   */
  static isSensitiveData(dataType: string): boolean {
    const sensitiveTypes = [
      'orders',
      'tableSessionSummary'
    ];
    
    return sensitiveTypes.includes(dataType);
  }

  /**
   * Audit cache access for security monitoring
   */
  static auditCacheAccess(
    operation: 'read' | 'write' | 'delete' | 'clear',
    key: string,
    userContext: UserContext,
    success: boolean
  ): void {
    if (import.meta.env.DEV) {
      const keyInfo = parseCacheKey(key);
      
      console.log('Cache Access Audit:', {
        operation,
        dataType: keyInfo.dataType,
        userId: userContext.userId,
        restaurantId: userContext.restaurantId,
        isStaff: userContext.isStaff,
        success,
        timestamp: new Date().toISOString()
      });
    }
  }
}

/**
 * Security middleware for cache operations
 */
export const withSecurity = <T extends any[], R>(
  userContext: UserContext,
  operation: string,
  fn: (...args: T) => Promise<R>
) => {
  return async (...args: T): Promise<R> => {
    try {
      const result = await fn(...args);
      
      // Audit successful operation
      if (args[0] && typeof args[0] === 'string') {
        CacheSecurityValidator.auditCacheAccess(
          operation as any,
          args[0],
          userContext,
          true
        );
      }
      
      return result;
    } catch (error) {
      // Audit failed operation
      if (args[0] && typeof args[0] === 'string') {
        CacheSecurityValidator.auditCacheAccess(
          operation as any,
          args[0],
          userContext,
          false
        );
      }
      
      throw error;
    }
  };
};

/**
 * Role-based cache access control
 */
export const CacheAccessControl = {
  /**
   * Check if user can read specific data type
   */
  canRead(dataType: string, userContext: UserContext): boolean {
    // All authenticated users can read basic data
    const publicDataTypes = [
      'branches',
      'categories', 
      'menuItems',
      'deliveryZones'
    ];
    
    if (publicDataTypes.includes(dataType)) {
      return true;
    }

    // Sensitive data requires explicit permission check
    const sensitiveDataTypes = [
      'orders',
      'tableSessionSummary'
    ];
    
    if (sensitiveDataTypes.includes(dataType)) {
      // For now, all authenticated users can read orders
      // Could be extended with more granular permissions
      return true;
    }

    return false;
  },

  /**
   * Check if user can write specific data type
   */
  canWrite(dataType: string, userContext: UserContext): boolean {
    // Staff might have limited write permissions
    if (userContext.isStaff) {
      // Check staff permissions for write operations
      // This could be expanded based on staff roles
      return ['orders', 'tableSessionSummary'].includes(dataType);
    }

    // Owners can write all data types
    return true;
  },

  /**
   * Check if user can delete cache entries
   */
  canDelete(dataType: string, userContext: UserContext): boolean {
    // Similar to write permissions
    return this.canWrite(dataType, userContext);
  }
};